/*
 * CIR-2026-361 — Theme preference logic.
 *
 * Calm low-stimulation palette is the default; the only theme axis is
 * light/dark. The user-facing preference is one of "system" | "light" | "dark"
 * (default "system", which resolves via prefers-color-scheme). The resolved
 * value ("light" | "dark") drives the `dark` class on <html>.
 *
 * Pure helpers live here so they can be unit-tested without a DOM. The no-flash
 * pre-paint bootstrap in public/theme-init.js mirrors this resolution; keep the
 * localStorage key and resolution rule in sync between the two.
 */

export const THEME_STORAGE_KEY = "spire-theme";

export type ThemePreference = "system" | "light" | "dark";
export type ResolvedTheme = "light" | "dark";

const PREFERENCES: readonly ThemePreference[] = ["system", "light", "dark"];

/** Narrow an arbitrary value to a valid ThemePreference, defaulting to "system". */
export function normalisePreference(value: unknown): ThemePreference {
  return PREFERENCES.includes(value as ThemePreference)
    ? (value as ThemePreference)
    : "system";
}

/** Resolve a preference to a concrete light/dark value given the OS preference. */
export function resolveTheme(
  preference: ThemePreference,
  prefersDark: boolean,
): ResolvedTheme {
  if (preference === "system") return prefersDark ? "dark" : "light";
  return preference;
}

/** Read the stored preference from localStorage, defaulting to "system". */
export function readStoredPreference(): ThemePreference {
  try {
    return normalisePreference(localStorage.getItem(THEME_STORAGE_KEY));
  } catch {
    return "system";
  }
}

/** True if the OS currently prefers a dark colour scheme. */
export function systemPrefersDark(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-color-scheme: dark)").matches
  );
}

/** Toggle the `dark` class on <html> for a resolved theme. */
export function applyResolvedTheme(resolved: ResolvedTheme): void {
  document.documentElement.classList.toggle("dark", resolved === "dark");
}
